import React, { useState, useRef } from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  Button,
  Input,
  Flex
} from '@chakra-ui/react';
import { Header } from '../components/Header';
import { fleetService, ValidationResultItem } from '../services/fleetService';

export const FleetPage: React.FC = () => {
  // Estado para controle do arquivo selecionado
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationResultItem[] | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const [processingErrors, setProcessingErrors] = useState<{ message: string; erros: Record<string, string[]> } | null>(null);
  const [openItems, setOpenItems] = useState<{[key: number]: boolean}>({});
  
  // Toggle a validation error item
  const toggleItem = (index: number) => {
    setOpenItems(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };
  const [showUploadForm, setShowUploadForm] = useState(true);
  
  // Referência para o input de arquivo
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Função para validar e processar o arquivo selecionado
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setUploadError(null);
    setValidationErrors(null);
    
    if (!file) {
      return;
    }
    
    // Validar se é um arquivo CSV
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setUploadError('Apenas arquivos CSV são aceitos');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }
    
    setSelectedFile(file);
  };
  
  // Função para realizar o upload do arquivo
  const handleUpload = async () => {
    if (!selectedFile) {
      setUploadError('Selecione um arquivo CSV para fazer o upload');
      return;
    }
    
    try {
      setIsUploading(true);
      setUploadError(null);
      setValidationErrors(null);
      setProcessingErrors(null);

      // Chamar o serviço de upload
      const response = await fleetService.uploadFleetCSV(selectedFile);

      // Limpar o arquivo selecionado
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      if (response.processingErrors) {
        // 200 OK mas com erros de processamento por linha
        setProcessingErrors({ message: response.message || 'Arquivo processado com críticas.', erros: response.processingErrors });
        setShowUploadForm(false);
      } else {
        // Sucesso total
        setUploadSuccess(response.message || 'Upload realizado com sucesso');
        setShowUploadForm(false);
      }
      
    } catch (error: any) {
      // Verificar se é um erro de validação
      if (error.validationError && error.status === 422) {
        setUploadError(error.message);
        setValidationErrors(error.validationResult);
      } else {
        setUploadError(error.message || 'Não foi possível processar o arquivo. Por favor, tente novamente.');
      }
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Box bg="#F5F5F5" minH="100vh" display="flex" flexDirection="column">
      <Header title="Sistema de Efetivação de Apólices" subtitle="Cadastro de Frota" />
      
      {/* Conteúdo principal em card separado */}
      <Box py={8} px={4} display="flex" justifyContent="center">
        <Box 
          maxW={{ base: "95%", md: "90%", lg: "80%", xl: "60%" }}
          w="100%"
          bg="white"
          borderRadius="xl"
          overflow="hidden"
          boxShadow="0 4px 20px rgba(0,0,0,0.08)"
        >
          <Box p={{ base: 4, md: 8 }}>
            <VStack gap={6} alignItems="stretch">
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Heading size="lg" color="#1A1A1A">Cadastro de Frotas</Heading>
              </Box>
              
              {/* Mensagens de alerta */}
              {uploadError && (
                <Box 
                  p={4} 
                  bg="red.50" 
                  borderRadius="md" 
                  borderLeft="4px solid" 
                  borderLeftColor="red.500"
                  position="relative"
                >
                  <Text color="red.700" fontWeight="medium" mb={validationErrors ? 2 : 0}>{uploadError}</Text>
                  
                  {/* Exibição de erros de validação — apenas itens com status "Erro" */}
                  {validationErrors && validationErrors.filter((e) => e.status === 'Erro').length > 0 && (
                    <VStack gap={2} mt={2} alignItems="stretch">
                      {validationErrors.filter((e) => e.status === 'Erro').map((error, index) => {
                        const isOpen = openItems[index] || false;
                        
                        return (
                          <Box key={index} border="none" mb={2}>
                            <Box 
                              bg="red.100" 
                              _hover={{ bg: "red.200" }}
                              borderRadius={isOpen ? "md md 0 0" : "md"}
                              px={3} 
                              py={2}
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                              onClick={() => toggleItem(index)}
                              cursor="pointer"
                            >
                              <Text fontSize="sm" fontWeight="medium">
                                {error.message}
                              </Text>
                              <Box 
                                as="button"
                                aria-label={isOpen ? "Recolher" : "Expandir"}
                                fontSize="1.4em"
                                lineHeight="1"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleItem(index);
                                }}
                              >
                                {isOpen ? '−' : '+'}
                              </Box>
                            </Box>
                            
                            {isOpen && (
                              <Box pb={4} pt={3} px={3} bg="red.50" borderBottomRadius="md">
                                <Text fontSize="sm" fontWeight="medium" mb={2}>Arquivo: {error.fileName}</Text>
                                
                                {Object.keys(error.chassis).length > 0 && (
                                  <Box>
                                    <Text fontSize="sm" fontWeight="medium" mb={1}>Detalhes:</Text>
                                    <Box pl={4}>
                                      {Object.entries(error.chassis).map(([key, value], idx) => (
                                        <Box key={idx} display="flex" alignItems="center" mb={1}>
                                          <Box as="span" mr={2}>•</Box>
                                          <Text fontSize="sm">
                                            <strong>{key}</strong>: {value}
                                          </Text>
                                        </Box>
                                      ))}
                                    </Box>
                                  </Box>
                                )}
                              </Box>
                            )}
                          </Box>
                        );
                      })}
                    </VStack>
                  )}
                  
                  <Box 
                    as="button"
                    position="absolute" 
                    right="8px" 
                    top="8px"
                    onClick={() => {
                      setUploadError(null);
                      setValidationErrors(null);
                    }}
                    fontSize="sm"
                    color="gray.500"
                    _hover={{ color: "gray.700" }}
                  >
                    ✕
                  </Box>
                </Box>
              )}
              
              {/* Erros de processamento: 200 OK mas com críticas por linha */}
              {processingErrors && (
                <Box
                  p={4}
                  bg="orange.50"
                  borderRadius="md"
                  borderLeft="4px solid"
                  borderLeftColor="orange.400"
                  position="relative"
                >
                  <Box
                    as="button"
                    position="absolute"
                    right="8px"
                    top="8px"
                    onClick={() => {
                      setProcessingErrors(null);
                      setShowUploadForm(true);
                    }}
                    fontSize="sm"
                    color="gray.500"
                    _hover={{ color: 'gray.700' }}
                  >
                    ✕
                  </Box>

                  <Flex alignItems="center" gap={2} mb={3}>
                    <Box
                      w="20px"
                      h="20px"
                      borderRadius="full"
                      bg="orange.400"
                      color="white"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      fontSize="xs"
                      fontWeight="bold"
                      flexShrink={0}
                    >
                      !
                    </Box>
                    <Text color="orange.800" fontWeight="bold">
                      Arquivo processado com críticas
                    </Text>
                  </Flex>

                  <Box
                    p={3}
                    bg="white"
                    borderRadius="md"
                    border="1px solid"
                    borderColor="orange.200"
                    mb={3}
                    fontSize="sm"
                    color="orange.900"
                    fontFamily="mono"
                  >
                    {processingErrors.message}
                  </Box>

                  <VStack gap={2} alignItems="stretch">
                    {Object.entries(processingErrors.erros).map(([linha, msgs]) => (
                      <Box key={linha} bg="orange.100" borderRadius="md" px={3} py={2}>
                        <Text fontSize="sm" fontWeight="bold" color="orange.800" mb={1}>
                          Linha {linha}
                        </Text>
                        {msgs.map((msg, i) => (
                          <Flex key={i} alignItems="flex-start" gap={2}>
                            <Box as="span" mt="2px" flexShrink={0}>•</Box>
                            <Text fontSize="sm" color="orange.900">{msg}</Text>
                          </Flex>
                        ))}
                      </Box>
                    ))}
                  </VStack>

                  <Flex mt={4} gap={2}>
                    <Button
                      size="sm"
                      bg="#3333CC"
                      color="white"
                      _hover={{ bg: '#2828A0' }}
                      onClick={() => {
                        setProcessingErrors(null);
                        setShowUploadForm(true);
                      }}
                    >
                      Tentar novamente
                    </Button>
                  </Flex>
                </Box>
              )}

              {uploadSuccess && (
                <Box 
                  p={6} 
                  bg="green.50" 
                  borderRadius="md" 
                  borderLeft="4px solid" 
                  borderLeftColor="green.500"
                  position="relative"
                  textAlign="center"
                >
                  <Flex direction="column" alignItems="center">
                    {/* Ícone de sucesso (check mark) */}
                    <Box 
                      width="60px" 
                      height="60px" 
                      borderRadius="full" 
                      bg="green.500" 
                      color="white"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      mb={3}
                    >
                      <svg width="30" height="30" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z" fill="currentColor"/>
                      </svg>
                    </Box>
                    
                    {/* Título de sucesso */}
                    <Heading size="md" color="green.700" mb={2}>Upload Concluído com Sucesso!</Heading>
                    
                    {/* Mensagem de detalhes no formato solicitado */}
                    <Box 
                      p={4} 
                      bg="white" 
                      borderRadius="md" 
                      border="1px solid" 
                      borderColor="green.200"
                      mb={4}
                      fontFamily="mono"
                      fontSize="sm"
                      color="green.800"
                    >
                      <Text fontWeight="bold" mb={1}>
                        {uploadSuccess}
                      </Text>
                    </Box>
                    
                    {/* Botões de ação */}
                    <Flex gap={3} mt={2}>
                      <Button 
                        bg="#3333CC"
                        color="white"
                        onClick={() => {
                          setUploadSuccess(null);
                          setShowUploadForm(true);
                        }}
                        _hover={{ bg: "#2828A0" }}
                      >
                        Subir Nova Frota
                      </Button>
                      <Button 
                        colorScheme="green" 
                        variant="outline"
                        onClick={() => setUploadSuccess(null)}
                      >
                        Fechar
                      </Button>
                    </Flex>
                  </Flex>
                  
                  <Box 
                    as="button"
                    position="absolute" 
                    right="8px" 
                    top="8px"
                    onClick={() => setUploadSuccess(null)}
                    fontSize="sm"
                    color="gray.500"
                    _hover={{ color: "gray.700" }}
                  >
                    ✕
                  </Box>
                </Box>
              )}
              
              {/* Formulário de upload */}
              {showUploadForm && (
                <Box 
                  p={4} 
                  bg="gray.50" 
                  borderRadius="md" 
                  borderWidth="1px"
                  borderColor="gray.200"
                >
                  <VStack gap={4} align="stretch">
                    <Text fontWeight="medium">Importar Cadastro de Frota</Text>
                    <Text fontSize="sm">
                      Selecione um arquivo CSV contendo os dados da frota para importação.
                      O arquivo deve estar no formato correto para processamento.
                    </Text>
                    
                    {/* Input de arquivo escondido */}
                    <Input
                      type="file"
                      accept=".csv"
                      onChange={handleFileChange}
                      ref={fileInputRef}
                      display="none"
                    />
                    
                    {/* Área visível - será apenas um botão se tiver arquivo ou área de drop se não tiver */}
                    {selectedFile ? (
                      <Box 
                        border="1px solid" 
                        borderColor="#3333CC" 
                        borderRadius="md"
                        p={4}
                        bg="blue.50"
                        textAlign="center"
                      >
                        <Box mb={3}>
                          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4C9.11 4 6.6 5.64 5.35 8.04C2.34 8.36 0 10.91 0 14C0 17.31 2.69 20 6 20H19C21.76 20 24 17.76 24 15C24 12.36 21.95 10.22 19.35 10.04Z" fill="#3333CC"/>
                          </svg>
                        </Box>
                        
                        <Text fontWeight="bold" color="#3333CC">
                          {selectedFile.name}
                        </Text>
                        <Text fontSize="xs" color="gray.600" mt={1}>
                          {(selectedFile.size / 1024).toFixed(2)} KB
                        </Text>
                      </Box>
                    ) : (
                      <Box 
                        border="1px dashed" 
                        borderColor="gray.300" 
                        borderRadius="md" 
                        p={6}
                        bg="gray.50"
                        textAlign="center"
                        cursor="pointer"
                        onClick={() => fileInputRef.current?.click()}
                        _hover={{ borderColor: "#3333CC", bg: "blue.50" }}
                        transition="all 0.2s"
                      >
                        <Box mb={3}>
                          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4C9.11 4 6.6 5.64 5.35 8.04C2.34 8.36 0 10.91 0 14C0 17.31 2.69 20 6 20H19C21.76 20 24 17.76 24 15C24 12.36 21.95 10.22 19.35 10.04ZM19 18H6C3.79 18 2 16.21 2 14C2 11.95 3.53 10.24 5.56 10.03L6.63 9.92L7.13 8.97C8.08 7.14 9.94 6 12 6C14.62 6 16.88 7.86 17.39 10.43L17.69 11.93L19.22 12.04C20.78 12.14 22 13.45 22 15C22 16.65 20.65 18 19 18ZM8 13H10.55V16H13.45V13H16L12 9L8 13Z" fill="#3333CC"/>
                          </svg>
                        </Box>
                        <Text fontWeight="medium">Área de upload do arquivo CSV</Text>
                        <Text fontSize="xs" color="gray.500" mt={1}>
                          Clique para selecionar o arquivo
                        </Text>
                      </Box>
                    )}
                    
                    <Box display="flex" justifyContent="flex-end" gap={2}>
                      {selectedFile && (
                        <Button 
                          variant="outline" 
                          onClick={() => {
                            setSelectedFile(null);
                            if (fileInputRef.current) {
                              fileInputRef.current.value = '';
                            }
                          }}
                        >
                          Remover arquivo
                        </Button>
                      )}
                      <Button 
                        bg="#3333CC"
                        color="white"
                        onClick={handleUpload}
                        disabled={!selectedFile || isUploading}
                        _hover={{ bg: "#2828A0" }}
                        _active={{ bg: "#1F1F80" }}
                      >
                        {isUploading ? "Enviando..." : "Enviar"}
                      </Button>
                    </Box>
                  </VStack>
                </Box>
              )}
              
            </VStack>
          </Box>
        </Box>
      </Box>
      
    </Box>
  );
};